import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Heart, ShoppingCart, Zap, Minus, Plus, ShieldCheck, Truck, Leaf, MessageCircle, ChevronRight, RotateCcw,
} from "lucide-react";
import { api } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { StarRating } from "@/components/StarRating";
import { ProductCard } from "@/components/ProductCard";
import { ReviewSection } from "@/components/ReviewSection";
import { formatINR, waLink } from "@/config";

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart, toggleWishlist, isWished, setCartOpen } = useStore();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [wIdx, setWIdx] = useState(0);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/products/${slug}`).then((r) => {
      setProduct(r.data);
      setWIdx(r.data.weights.length > 1 ? 1 : 0);
      setQty(1);
      api.get("/products", { params: { category: r.data.category } }).then((rr) =>
        setRelated(rr.data.filter((p) => p.slug !== slug).slice(0, 4))
      );
    }).catch(() => navigate("/shop"));
  }, [slug]); // eslint-disable-line

  if (!product) {
    return <div className="mx-auto max-w-7xl px-4 py-20 text-center text-[#6B5B52]">Loading product...</div>;
  }

  const w = product.weights[wIdx];
  const off = w.mrp > w.price ? Math.round(((w.mrp - w.price) / w.mrp) * 100) : 0;
  const wished = isWished(product.slug);

  const waMsg = `Hi! I'm interested in *${product.name}* (${w.label}) — ${formatINR(w.price)}. Qty: ${qty}. Please assist.`;

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-6">
      <nav className="flex items-center gap-1.5 text-xs text-[#6B5B52] mb-5">
        <Link to="/" className="hover:text-[#C2410C]">Home</Link><ChevronRight size={13} />
        <Link to="/shop" className="hover:text-[#C2410C]">Shop</Link><ChevronRight size={13} />
        <span className="text-[#1F130B] font-medium truncate">{product.name}</span>
      </nav>

      <div className="grid lg:grid-cols-2 gap-10">
        <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }}>
          <div className="rounded-3xl overflow-hidden border border-[#E7E0D6] bg-white aspect-square">
            <img src={product.image} alt={product.name} className="h-full w-full object-cover" />
          </div>
        </motion.div>

        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs uppercase tracking-widest text-[#C2410C] font-semibold">{product.origin}</span>
            {product.badges?.map((b) => (
              <span key={b} className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-[#FEF3C7] text-[#9A3412]">{b.replace("-", " ")}</span>
            ))}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#1F130B] mt-2" data-testid="product-title">{product.name}</h1>
          <div className="mt-2"><StarRating value={product.rating} count={product.reviews_count} showValue /></div>
          <p className="mt-4 text-[#6B5B52] leading-relaxed">{product.description}</p>

          <div className="mt-5 flex items-end gap-3">
            <span className="font-display text-4xl font-bold text-[#1F130B]" data-testid="product-price">{formatINR(w.price)}</span>
            {w.mrp > w.price && <span className="text-lg text-[#9CA3AF] line-through mb-1">{formatINR(w.mrp)}</span>}
            {off > 0 && <span className="mb-1.5 text-sm font-bold text-[#15803D]">{off}% OFF</span>}
          </div>
          <p className="text-xs text-[#6B5B52] mt-1">Inclusive of all taxes · MRP includes packaging</p>

          <div className="mt-6">
            <p className="text-sm font-semibold text-[#1F130B] mb-2">Select Weight</p>
            <div className="flex flex-wrap gap-2.5">
              {product.weights.map((ww, i) => (
                <button
                  key={ww.label}
                  onClick={() => setWIdx(i)}
                  data-testid={`detail-weight-${ww.label}`}
                  className={`rounded-xl px-4 py-2.5 text-sm font-semibold border-2 transition-colors ${
                    i === wIdx ? "border-[#C2410C] bg-[#FEF3C7] text-[#9A3412]" : "border-[#E7E0D6] bg-white text-[#6B5B52] hover:border-[#C2410C]"
                  }`}
                >
                  <span className="block">{ww.label}</span>
                  <span className="block text-xs font-normal">{formatINR(ww.price)}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="mt-6 flex items-center gap-4">
            <div className="flex items-center border-2 border-[#E7E0D6] rounded-full bg-white">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="detail-qty-dec" className="h-11 w-11 grid place-items-center text-[#6B5B52]"><Minus size={16} /></button>
              <span className="w-10 text-center font-semibold" data-testid="detail-qty">{qty}</span>
              <button onClick={() => setQty((q) => Math.min(99, q + 1))} data-testid="detail-qty-inc" className="h-11 w-11 grid place-items-center text-[#6B5B52]"><Plus size={16} /></button>
            </div>
            <button
              onClick={() => toggleWishlist(product.slug)}
              data-testid="detail-wishlist-btn"
              className="h-11 w-11 grid place-items-center rounded-full border-2 border-[#E7E0D6] bg-white hover:border-[#C2410C]"
            >
              <Heart size={19} className={wished ? "fill-[#C2410C] text-[#C2410C]" : "text-[#6B5B52]"} />
            </button>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => { addToCart(product, w, qty); setCartOpen(true); }}
              data-testid="detail-add-to-cart"
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#C2410C] hover:bg-[#9A3412] text-white font-semibold py-3.5 transition-colors"
            >
              <ShoppingCart size={18} /> Add to Cart
            </button>
            <button
              onClick={() => { addToCart(product, w, qty, true); navigate("/checkout"); }}
              data-testid="detail-buy-now"
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-full border-2 border-[#D97706] text-[#9A3412] hover:bg-[#FEF3C7] font-semibold py-3.5 transition-colors"
            >
              <Zap size={18} /> Buy Now
            </button>
          </div>
          <a
            href={waLink(waMsg)}
            target="_blank"
            rel="noreferrer"
            data-testid="detail-whatsapp-btn"
            className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] hover:brightness-95 text-white font-semibold py-3.5 transition"
          >
            <MessageCircle size={18} /> Order on WhatsApp
          </a>

          <div className="mt-7 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[[ShieldCheck, "100% Pure"], [Leaf, "Freshly Packed"], [Truck, "Fast Delivery"], [RotateCcw, "Easy Returns"]].map(([Icon, l]) => (
              <div key={l} className="flex flex-col items-center text-center gap-1.5 rounded-xl bg-white border border-[#E7E0D6] p-3">
                <Icon size={20} className="text-[#C2410C]" />
                <span className="text-xs font-medium text-[#6B5B52]">{l}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-16"><ReviewSection slug={product.slug} title="Product Reviews" /></div>

      {related.length > 0 && (
        <div className="mt-16">
          <h2 className="font-display text-2xl font-bold text-[#1F130B] mb-6">You May Also Like</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {related.map((p) => <ProductCard key={p.slug} product={p} />)}
          </div>
        </div>
      )}
    </div>
  );
}
